import React from 'react';
import { CliOperation } from '../../types';
import { formatDateTimeUTC, formatDuration } from '../../utils/dateTime';
import { Badge } from '../StatusBadge';
import { maskApiKey } from '../../utils/apiKey';
import UsageDetailView, { Field, UsageDetailPage, UsageEntriesQuery } from './UsageDetailView';

export { Field };
export type { UsageDetailPage, UsageEntriesQuery };

interface CliOperationViewProps<T extends CliOperation> {
    id: string | undefined;
    backTo: string;
    pageSizeKey: string;
    load: (id: string, query: UsageEntriesQuery) => Promise<UsageDetailPage<T>>;
    extraFields?: (operation: T) => React.ReactNode;
    // Admin sees output tokens. Users do not.
    showOutputTokens?: boolean;
}

// The second timestamp depends on the status: a lease still runs, an
// expired one died at its deadline, a finished one has a completion time.
function endField(operation: CliOperation): { label: string; time: string | null } {
    if (operation.status === 'authorized') return { label: 'Expires At', time: operation.expires_at };
    if (operation.status === 'expired') return { label: 'Expired At', time: operation.expires_at };
    return { label: 'Completed At', time: operation.completed_at };
}

// CLI operation details on the shared usage detail view.
function CliOperationView<T extends CliOperation>({
    id,
    backTo,
    pageSizeKey,
    load,
    extraFields,
    showOutputTokens = false,
}: CliOperationViewProps<T>) {
    return (
        <UsageDetailView<T>
            id={id}
            backTo={backTo}
            pageSizeKey={pageSizeKey}
            load={load}
            showOutputTokens={showOutputTokens}
            noun="operation"
            title={(operation) => operation.project_name}
            titleAside={(operation) => <Badge value={operation.status} />}
            notice={(operation) => operation.error_summary}
            // Usage entries exist only for a success complete: failed completes carry no
            // entries, and expired or in-flight operations never recorded any.
            showUsage={(operation) => operation.status === 'success'}
            fields={(operation) => {
                const end = endField(operation);
                return (
                    <>
                        <Field label="Operation">{operation.operation}</Field>
                        <Field label="API Key">
                            {operation.api_key_name}
                            <span className="text-secondary font-mono ml-2">{maskApiKey(operation)}</span>
                        </Field>
                        <Field label="Files">{operation.file_count ?? '—'}</Field>
                        <Field label="Version">{operation.alchemist_version}</Field>
                        <Field label="Authorized At">{formatDateTimeUTC(operation.authorized_at)}</Field>
                        <Field label={end.label}>{formatDateTimeUTC(end.time)}</Field>
                        <Field label="Duration">
                            {operation.completed_at ? formatDuration(operation.authorized_at, operation.completed_at) : '—'}
                        </Field>
                        <Field label="Client Run ID">
                            <span className="font-mono">{operation.client_run_id}</span>
                        </Field>
                        {extraFields?.(operation)}
                    </>
                );
            }}
        />
    );
}

export default CliOperationView;
